import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext, useDocument } from "@automerge/react";

import "@/components/mosaic.css";
import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  Navigate,
  getRouteApi,
  useRouter,
  useMatchRoute,
} from "@tanstack/react-router";
import type { ShowInput } from "apprentices/tools/show";
import {
  useCheckpointRail,
  type CheckpointRailState,
  type RailCheckpoint,
} from "forest/chat/use-checkpoint-rail";
import { useTreeExpansion } from "forest/file-tree/use-expansion";
import { useFileCommands } from "forest/file-tree/use-file-commands";
import { useHiddenFiles } from "forest/file-tree/use-hidden-files";
import { useMaterializedFiles } from "forest/file-tree/use-materialized-files";
import { useTreeEntries } from "forest/file-tree/use-tree";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";
import {
  Box,
  Code2,
  History,
  KeyboardIcon,
  MapPinPlus,
  PencilIcon,
  Trash2,
  XIcon,
} from "lucide-react";
import {
  type AnnotatedDoc,
  textAnnotations,
} from "playground/annotations/annotations";
import { forkBranchFile } from "playground/branches/project-branch";
import { resolveModelEntry } from "playground/builder/entry";
import { isMountInode } from "playground/fs/mount";
import type { TreeEntry } from "playground/fs/paths";
import type { PlayFileInode } from "playground/fs/types";
import { migrateProjectFiles } from "playground/project";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Mosaic,
  MosaicWindow,
  MosaicWindowContext,
  getLeaves,
  type MosaicNode,
  type MosaicPath,
  type TabButtonRenderer,
  type TabGroupContext,
} from "react-mosaic-component";
import { toast } from "sonner";
import { invariant } from "ts-invariant";

import { parseCameraView } from "@/bear-viewer/camera-view";
import { AskBearProvider, useAskBearDispatch } from "@/comments/ask-bear";
import { CommentsButton, FileComments } from "@/comments/FileComments";
import BearSandboxProvider, {
  useBearSandbox,
} from "@/components/BearSandboxProvider";
import PaneSuspense from "@/components/PaneSuspense";
import type { CommandMenuGroup } from "@/components/ProjectCommandMenu";
import { ProjectContentLoader } from "@/components/ProjectContentLoader";
import ProjectFileIcon from "@/components/ProjectFileIcon";
import { PROJECT_FILE_PROXY_PATH } from "@/components/ProjectFileLink";
import ProjectFileList, {
  type ProjectPanelTab,
} from "@/components/ProjectFileList";
import { useProjectShellCommands } from "@/components/ProjectShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMac } from "@/hooks/use-is-mac";
import { JigsProvider } from "@/jigs/JigsProvider";
import { isAutomergeUrl } from "@/lib/automerge-repo";
import { useBlobSrc } from "@/lib/blob-src";
import { useEditorSettings } from "@/lib/editor-settings";
import BearModelPane from "@/panes/BearModelPane";
import BuilderPane from "@/panes/BuilderPane";
import EditorPane from "@/panes/EditorPane";
import { useFollowConversation } from "@/panes/follow-conversation";
import HistoryPane from "@/panes/HistoryPane";
import {
  markupViewOf,
  type MarkupView,
  type ModelMarkupHost,
} from "@/panes/model-markup";
import {
  activatePane,
  adjustLayoutForActivePanes,
  isTabbedTile,
  tabbedLayoutForPanes,
  visiblePaneIds,
  type PaneId,
} from "@/panes/mosaic-layout";
import PaneTab from "@/panes/PaneTab";
import PdfPane from "@/panes/PdfPane";
import { projectTools } from "@/panes/project-tool-registry";
import type { ProjectTool } from "@/panes/project-tools";
import RecordsPane from "@/panes/RecordsPane";
import SketchPane, { type SketchReveal } from "@/panes/SketchPane";
import { useSharedProjectChats } from "@/panes/use-project-chats";

import "react-mosaic-component/react-mosaic-component.css";
import { PaneActions, useWorkspace } from "@/panes/Workspace";
import type { Project } from "@/types/projects";

import "./p.$projectId/studio-theme.css";

type FileKind =
  | "doc"
  | "code"
  | "sketch"
  | "pdf"
  | "records"
  | "history"
  | "plaintext";

type FileTab = {
  id: string;
  filename: string;
  kind: FileKind;
  title: string;
};

const EMPTY_PANE_ID = "empty";
const FILE_TAB_PREFIX = "file:";
const HISTORY_TAB_PREFIX = "history:";
/** Kinds that share one tab stack: everything the editor pane can open. */
const EDITOR_KINDS = new Set<FileKind>(["doc", "code", "plaintext"]);
const route = getRouteApi("/p/$projectId/$branch/explorer");
const projectRoute = getRouteApi("/p/$projectId");

function getFileKind(filename: string): FileKind | null {
  const extension = filename.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "md":
    case "mdx":
      return "doc";
    case "ts":
    case "tsx":
      return "code";
    case "tldr":
      return "sketch";
    case "pdf":
      return "pdf";
    case "records":
      return "records";
    default:
      return null;
  }
}

/**
 * Every file the palette can open, by full path: the project's own files, not
 * mounted library content. Dot-entries follow the file tree's own rule (hidden
 * at any depth) so both surfaces show the same project.
 */
function openableFilePaths(
  entries: readonly TreeEntry[],
  showHidden: boolean,
): string[] {
  const mounts = entries
    .filter((e) => isMountInode(e.inode))
    .map((e) => `${e.path}/`);
  return entries
    .filter(
      ({ path, isDirectory }) =>
        !isDirectory &&
        getFileKind(path) !== null &&
        !mounts.some((m) => path.startsWith(m)) &&
        (showHidden || !path.split("/").some((s) => s.startsWith("."))),
    )
    .map((e) => e.path)
    .sort((a, b) => a.localeCompare(b));
}

function resolveProjectFileHref(reference?: string): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (reference.startsWith("automerge:")) {
    return undefined;
  }

  if (reference.startsWith(PROJECT_FILE_PROXY_PATH)) {
    return reference;
  }

  if (reference.startsWith("s3://")) {
    return `${PROJECT_FILE_PROXY_PATH}?source=${encodeURIComponent(reference)}`;
  }

  return reference;
}

/** Stamped on every doc a restore rewrites, so the file history says why. */
const restoreMessage = (checkpoint: RailCheckpoint) =>
  `Restored checkpoint: ${checkpoint.description}`;

function basename(path: string): string {
  return path.split("/").pop() ?? path;
}

function getFileTabId(filename: string): string {
  return `${FILE_TAB_PREFIX}${filename}`;
}

function getHistoryTabId(filename: string): string {
  return `${HISTORY_TAB_PREFIX}${filename}`;
}

function createFileTab(
  filename: string,
  kind: FileKind,
  existingTabs: FileTab[],
): FileTab {
  let title = filename;

  if (kind === "sketch") {
    const baseName = filename.replace(/\.tldr$/i, "");
    if (baseName.length > 0) {
      title = baseName;
    } else {
      const sketchCount = existingTabs.filter(
        (tab) => tab.kind === "sketch",
      ).length;
      title = `Sketch ${sketchCount + 1}`;
    }
  }

  if (kind === "history") {
    title = `${filename} History`;
  }

  const id =
    kind === "history" ? getHistoryTabId(filename) : getFileTabId(filename);

  return {
    id,
    filename,
    kind,
    title,
  };
}

export const Route = createFileRoute("/p/$projectId/$branch/explorer")({
  pendingMs: 0,
  pendingComponent: ProjectContentLoader,
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "model"; cam?: string; plane?: string; thread?: string } => ({
    view: search.view === "model" ? ("model" as const) : undefined,
    cam:
      typeof search.cam === "string" && parseCameraView(search.cam)
        ? search.cam
        : undefined,
    plane: typeof search.plane === "string" ? search.plane : undefined,
    thread: typeof search.thread === "string" ? search.thread : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { indexUrl: mainIndexUrl } = projectRoute.useLoaderData();
  const { task } = getRouteApi("/p/$projectId/$branch").useLoaderData();
  const indexUrl = task?.branch_url ?? mainIndexUrl;
  const [project, changeProject] = useDocument<Project>(
    indexUrl as AutomergeUrl,
    {
      suspense: true,
    },
  );

  if (task && task.status !== "open") {
    return (
      <Navigate
        to="/p/$projectId/$branch/workbench/$view"
        params={{ projectId, branch: task.id, view: "changes" }}
        search={{}}
      />
    );
  }

  return (
    <BearSandboxProvider
      project={project}
      projectId={projectId}
      indexUrl={indexUrl as AutomergeUrl}
      taskId={task?.id}
    >
      {/* Keyed by project: the route component is REUSED across
                  $projectId navigations, and every piece of state below
                  belongs to one project: the open chat, the open file
                  tabs, the mosaic layout, the checkpoint dialog. A
                  carried-over chat id would address `${newProject}:
                  ${oldChat}`, a cross-project thread (Codex, #1476). One
                  key here rather than one per pane: a different project is
                  a different workspace, and the panes can't each remember. */}
      {/* The agent panel's jig tools run in this branch's jigs. */}
      <JigsProvider projectId={projectId}>
        <ProjectWorkspace
          key={`${projectId}:${task?.id ?? "main"}`}
          project={project}
          changeProject={changeProject}
          indexUrl={indexUrl as AutomergeUrl}
          task={task}
        />
      </JigsProvider>
    </BearSandboxProvider>
  );
}

interface ProjectWorkspaceProps {
  project: Project;
  changeProject(update: (doc: Project) => void): void;
  indexUrl: AutomergeUrl;
  task: { id: string; title: string; branch_url: string } | null;
}

function ProjectWorkspace({
  project,
  changeProject,
  indexUrl,
  task,
}: ProjectWorkspaceProps) {
  const { projectId, branch } = route.useParams();
  const { canManage } = projectRoute.useLoaderData();
  const { view: fileView, cam, plane, thread } = route.useSearch();
  const matchRoute = useMatchRoute();
  const fileMatch = matchRoute({
    to: "/p/$projectId/$branch/explorer/files/$",
  });
  const initialFile = fileMatch ? fileMatch._splat : undefined;
  const modelFile = fileView === "model" ? initialFile : undefined;
  const router = useRouter();
  const navigateMarkup = useCallback(
    (view: MarkupView, mode: "push" | "replace") => {
      invariant(modelFile, "No model view to put a markup view in");
      void router.navigate({
        to: "/p/$projectId/$branch/explorer/files/$",
        params: { projectId, branch, _splat: modelFile },
        search: {
          view: "model",
          cam: view.cam,
          plane: view.plane,
          thread: view.thread,
        },
        replace: mode === "replace",
      });
    },
    [branch, modelFile, projectId, router],
  );
  const markupHost = useMemo<ModelMarkupHost | undefined>(
    () =>
      modelFile
        ? {
            file: modelFile,
            indexUrl,
            cam,
            plane,
            thread,
            navigate: navigateMarkup,
          }
        : undefined,
    [cam, indexUrl, modelFile, navigateMarkup, plane, thread],
  );
  const { vimMode, setVimMode } = useEditorSettings();
  const isMac = useIsMac();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState(project.name);

  // Set page title to include project name
  useEffect(() => {
    document.title = `${project.name} — Bear Studio`;
    return () => {
      document.title = "Bear Studio";
    };
  }, [project.name]);

  // Migrate old Record<string, string> files to PlayFileInode format
  useEffect(() => {
    const needsMigration = Object.values(project.files).some(
      (v) => typeof v === "string",
    );
    if (needsMigration && !task) {
      changeProject((doc) => {
        migrateProjectFiles(doc);
      });
    }
  }, [project.files, changeProject, task]);

  const handleRename = useCallback(
    (nextName: string) => {
      changeProject((doc) => {
        doc.name = nextName;
      });
    },
    [changeProject],
  );

  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete project");
    },
    onSuccess: () => {
      toast.success("Project deleted");
      router.navigate({ to: "/" });
    },
    onError: () => {
      toast.error("Failed to delete project");
    },
  });

  const [filesPanelManualSize, setFilesPanelManualSize] = useState<number>();
  const [builderPanelManualSize, setBuilderPanelManualSize] =
    useState<number>();
  // The open conversation is the shell's: it stays open across branches, and
  // its panel sits beside whatever is open here, on this branch.
  const chats = useSharedProjectChats();
  const isBuilderPaneOpen = chats.open !== null;
  const startDraft = chats.startDraft;
  const followConversation = useFollowConversation(projectId);
  // A comment addressed to Bear goes to the panel's conversation.
  const askBear = useAskBearDispatch({
    projectId,
    taskId: task?.id,
    chatId: chats.open?.kind === "thread" ? chats.open.id : null,
    open: chats.openThread,
  });
  const [filesPanelTab, setFilesPanelTab] = useState<ProjectPanelTab>("files");
  const [fileTabs, setFileTabs] = useState<FileTab[]>([]);
  const [dirtyFiles, setDirtyFiles] = useState<Set<string>>(new Set());
  const [openToolIds, setOpenToolIds] = useState<string[]>([]);
  const [focusedPaneId, setFocusedPaneId] = useState<string>(EMPTY_PANE_ID);
  const [landingResolved, setLandingResolved] = useState(Boolean(initialFile));
  const [mosaicLayout, setMosaicLayout] = useState<MosaicNode<PaneId> | null>(
    null,
  );
  const { focusPane: focusWorkspacePane } = useWorkspace();
  const repo = useContext(RepoContext);
  const { run, playground } = useBearSandbox();
  const toolById = useMemo(
    () =>
      new Map<string, ProjectTool>(projectTools.map((tool) => [tool.id, tool])),
    [],
  );
  // The whole tree (nested and linked dirs included), for sync render lookups.
  const treeEntries = useTreeEntries(playground);
  const inodeAt = useMemo(
    () => new Map(treeEntries?.map((e) => [e.path, e.inode])),
    [treeEntries],
  );
  // Which directories are open. Held here, not in the tree, because it is what
  // decides which mounts get listed at all.
  const expansion = useTreeExpansion();
  // The doc tree with an opened mount's children (a published package's
  // sources) spliced in, so the list shows library content the doc can't hold.
  const treeFiles = useMaterializedFiles(
    project.files,
    playground,
    expansion.expanded,
  );
  // Dot-entries hide by default, with the OS chords (⌘⇧. / Ctrl+H) as the
  // reveal. One listener per project view.
  const { showHidden: showHiddenFiles } = useHiddenFiles();
  const showPanelTab = setFilesPanelTab;
  const rail = useCheckpointRail({
    repo,
    indexUrl,
    checkpointsDocUrl: project.checkpointsDocUrl,
    files: project.files,
    restoreMessage,
  });
  // The model is a projection of the project files, so a restore that put them
  // back leaves the viewport drawing a state the project is no longer in.
  const checkpoints = useMemo<CheckpointRailState>(
    () => ({
      ...rail,
      restore: async (checkpoint) => {
        await rail.restore(checkpoint);
        await run();
      },
    }),
    [rail, run],
  );
  const handleRun = useCallback(async () => {
    await run();
  }, [run]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  // Owned here, not in the file list, so the ⌘K action opens the same dialog.
  const [isCheckpointDialogOpen, setIsCheckpointDialogOpen] = useState(false);

  const allPaneIds = useMemo(
    () => [
      ...openToolIds.filter((id) => toolById.has(id)),
      ...fileTabs.map((tab) => tab.id),
    ],
    [fileTabs, openToolIds, toolById],
  );
  const hasOpenPanes = allPaneIds.length > 0;
  // New panes join the visible pane as tabs. A split made by dragging a tab
  // stays in place when more files are opened.
  useEffect(() => {
    setMosaicLayout((previous) => tabbedLayoutForPanes(previous, allPaneIds));
  }, [allPaneIds]);

  // Focusing a pane brings its tab forward, so opening a file already behind
  // a sibling tab shows it.
  useEffect(() => {
    setMosaicLayout((previous) => activatePane(previous, focusedPaneId));
  }, [focusedPaneId]);

  // The layout is where a pane is closed — a window's close button or a tab's
  // — so what is open follows it. And a tab click is how a pane comes forward,
  // so focus follows that too: typing lands where the user is looking.
  const handleLayoutChange = useCallback(
    (layout: MosaicNode<PaneId> | null) => {
      const tabbed = tabbedLayoutForPanes(
        layout,
        layout ? getLeaves(layout) : [],
      );
      setMosaicLayout(tabbed);
      setFocusedPaneId((current) => visiblePaneIds(tabbed)[0] ?? current);
      const placed = new Set(layout ? getLeaves(layout) : []);
      setFileTabs((previous) => {
        const next = previous.filter((tab) => placed.has(tab.id));
        return next.length === previous.length ? previous : next;
      });
      setOpenToolIds((previous) => {
        const next = previous.filter((id) => placed.has(id));
        return next.length === previous.length ? previous : next;
      });
    },
    [],
  );

  useEffect(() => {
    focusWorkspacePane(focusedPaneId);
  }, [focusWorkspacePane, focusedPaneId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const mod = isMac ? event.metaKey : event.ctrlKey;
      if (mod) {
        const key = event.key.toLowerCase();
        if (key === "j") {
          event.preventDefault();
          startDraft();
        } else if (key === "enter") {
          event.preventDefault();
          handleRun();
        } else if (key === "s") {
          // Prevent browser save dialog — edits auto-sync via Automerge
          event.preventDefault();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMac, handleRun, startDraft]);

  const handleOpenFile = useCallback(
    (
      filename: string,
      options?: {
        focus?: boolean;
        bypassValidation?: boolean;
        asPlainText?: boolean;
        source?: boolean;
      },
    ) => {
      const kind = options?.asPlainText ? "plaintext" : getFileKind(filename);
      if (!kind) {
        return;
      }
      const openTab = () => {
        const tabId = getFileTabId(filename);
        setFileTabs((prev) => {
          if (prev.some((tab) => tab.id === tabId)) return prev;
          return [...prev, createFileTab(filename, kind, prev)];
        });
        if (options?.focus ?? true) {
          setFocusedPaneId(tabId);
          if (initialFile !== filename || (options?.source && fileView)) {
            void router.navigate({
              to: "/p/$projectId/$branch/explorer/files/$",
              params: { projectId, branch, _splat: filename },
              search: { view: undefined },
            });
          }
        }
      };
      if (!task) {
        openTab();
        return;
      }
      void (async () => {
        const inode = await playground.stat(filename);
        if (inode?.version?.kind === "automerge" && !isMountInode(inode)) {
          invariant(repo, "Automerge repo is not available");
          await forkBranchFile(repo, indexUrl, filename);
        }
        openTab();
      })().catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : String(error));
      });
    },
    [
      fileView,
      indexUrl,
      initialFile,
      playground,
      projectId,
      repo,
      router,
      task,
    ],
  );

  useEffect(() => {
    if (initialFile) handleOpenFile(initialFile);
  }, [initialFile, handleOpenFile]);

  useEffect(() => {
    if (initialFile || landingResolved) return;
    let live = true;
    void resolveModelEntry(async (path) => {
      const file = await playground.open(path);
      try {
        return file.kind === "text" ? await file.read() : "";
      } finally {
        await file.close();
      }
    }).then(
      (entry) => {
        if (!live) return;
        setLandingResolved(true);
        void router.navigate({
          to: "/p/$projectId/$branch/explorer/files/$",
          params: { projectId, branch, _splat: entry },
          search: { view: "model" },
          replace: true,
        });
      },
      () => {
        if (live) setLandingResolved(true);
      },
    );
    return () => {
      live = false;
    };
  }, [initialFile, landingResolved, playground, projectId, router, task]);

  /**
   * Where a "go to definition" jump landed, and what it was aiming at.
   *
   * Held as one object rather than cleared after use: the editor reveals on
   * identity change, so a stale value sitting here is inert, and the same jump
   * repeated makes a new object and reveals again.
   */
  const [jumpTarget, setJumpTarget] = useState<{
    path: string;
    selection?: EditorSelection;
    heading?: { text: string };
    shapes?: SketchReveal;
    thread?: { threadId: string };
  } | null>(null);
  // The file whose comment list is open over its pane.
  const [commentsFor, setCommentsFor] = useState<string | null>(null);

  const handleOpenPath = useCallback(
    (path: string, selection?: EditorSelection) => {
      handleOpenFile(path, { source: true });
      setJumpTarget({ path, selection });
    },
    [handleOpenFile],
  );

  /** The agent's `show` tool: open the pane, aim the reveal, say what is up. */
  const handleShow = useCallback(
    async (input: ShowInput): Promise<string> => {
      if (input.kind === "model") {
        const entry = await resolveModelEntry(async (path) => {
          const file = await playground.open(path);
          try {
            return file.kind === "text" ? await file.read() : "";
          } finally {
            await file.close();
          }
        });
        await router.navigate({
          to: "/p/$projectId/$branch/explorer/files/$",
          params: { projectId, branch, _splat: entry },
          search: { view: "model" },
        });
        return `3D view of ${entry} open`;
      }
      if (input.kind === "comment") {
        const uri = (await playground.stat(input.path))?.uri;
        invariant(repo && isAutomergeUrl(uri), `${input.path} has no comments`);
        const handle = await repo.find<AnnotatedDoc>(uri);
        await handle.whenReady();
        const doc = handle.doc();
        const found = doc?.[`comment:${input.threadId}`];
        if (!found) {
          return `${input.path} has no comment thread ${input.threadId}`;
        }
        // A redline opens on its plane, square-on, with its card open.
        const markup = markupViewOf(doc, input.threadId);
        if (markup) {
          await router.navigate({
            to: "/p/$projectId/$branch/explorer/files/$",
            params: { projectId, branch, _splat: input.path },
            search: { view: "model", ...markup },
          });
          return `${input.path} open on its markup plane, at thread ${input.threadId}`;
        }
        // A thread whose quoted text is gone opens in the file's comment list.
        const anchored = textAnnotations(doc).some(
          ({ annotation, range }) =>
            annotation.id === input.threadId && range !== null,
        );
        handleOpenFile(input.path, { source: true });
        setJumpTarget({
          path: input.path,
          thread: { threadId: input.threadId },
        });
        if (!anchored) setCommentsFor(input.path);
        return `${input.path} open on thread ${input.threadId}`;
      }
      if (input.kind === "sketch") {
        handleOpenFile(input.path);
        setJumpTarget({
          path: input.path,
          shapes: { ids: input.shapes ?? [] },
        });
        return input.shapes?.length
          ? `${input.path} zoomed to ${input.shapes.join(", ")}, selected`
          : `${input.path} fit to the whole sketch`;
      }
      // A rendered markdown doc has headings and no line numbers; source has
      // the reverse. Refuse the selector the pane would ignore.
      const rendered =
        getFileKind(input.path) === "doc" &&
        isAutomergeUrl((await playground.stat(input.path))?.uri);
      invariant(
        !input.heading || rendered,
        `"${input.path}" opens as source, not rendered markdown: use line, not heading`,
      );
      invariant(
        !input.line || !rendered,
        `"${input.path}" opens as rendered markdown: use heading, not line`,
      );
      handleOpenFile(input.path, { source: true });
      setJumpTarget({
        path: input.path,
        selection: input.line
          ? { startLineNumber: input.line, startColumn: 1 }
          : undefined,
        heading: input.heading ? { text: input.heading } : undefined,
      });
      return input.heading
        ? `${input.path} at "${input.heading}"`
        : input.line
          ? `${input.path} at line ${input.line}`
          : `${input.path} open`;
    },
    [branch, handleOpenFile, playground, projectId, repo, router],
  );

  const handleOpenAsPlainText = useCallback(
    (filename: string, options?: { focus?: boolean }) => {
      handleOpenFile(filename, { ...options, asPlainText: true });
    },
    [handleOpenFile],
  );

  const handleOpenHistory = useCallback(
    async (filename: string, options?: { focus?: boolean }) => {
      const inode = await playground.stat(filename);
      invariant(
        isAutomergeUrl(inode?.uri),
        "History is only available for Automerge documents.",
      );
      const shouldFocus = options?.focus ?? true;
      const tabId = getHistoryTabId(filename);
      setFileTabs((prev) => {
        if (prev.some((tab) => tab.id === tabId)) {
          return prev;
        }
        return [...prev, createFileTab(filename, "history", prev)];
      });
      if (shouldFocus) {
        setFocusedPaneId(tabId);
      }
    },
    [playground],
  );

  // How the workspace puts itself back together after a file mutation,
  // wherever it came from: the row menu, the Files header, or a tool. The
  // shared commands fire these, so no call site has to remember.
  const fileMutated = useMemo(
    () => ({
      created: (path: string) =>
        handleOpenFile(path, { bypassValidation: true }),
      // A deleted file (or anything under a deleted folder) can't stay open.
      deleted: (path: string) =>
        setFileTabs((prev) =>
          prev.filter(
            (tab) =>
              tab.filename !== path && !tab.filename.startsWith(`${path}/`),
          ),
        ),
      // Renamed files keep their tab under the new path; a folder rename
      // carries every tab beneath it.
      renamed: (from: string, to: string) =>
        setFileTabs((prev) =>
          prev.map((tab) =>
            tab.filename === from
              ? createFileTab(to, tab.kind, prev)
              : tab.filename.startsWith(`${from}/`)
                ? createFileTab(
                    `${to}${tab.filename.slice(from.length)}`,
                    tab.kind,
                    prev,
                  )
                : tab,
          ),
        ),
    }),
    [handleOpenFile],
  );

  const commands = useFileCommands(playground, fileMutated);
  // A naming session has to be visible to be typed into, and any surface can
  // start one (row menu, header button, ⌘K tool). So the view reveals it (the
  // Files pane, and the directory it lands in) rather than each caller
  // remembering to.
  const editKey = commands.edit?.key ?? null;
  const editDir = commands.edit?.dir ?? null;
  const revealDir = expansion.reveal;
  useEffect(() => {
    if (editDir === null) return;
    showPanelTab("files");
    revealDir(editDir);
  }, [editKey, editDir, revealDir, showPanelTab]);

  const handleOpenTool = useCallback(
    (toolId: string, options?: { focus?: boolean }) => {
      const tool = toolById.get(toolId);
      if (!tool) {
        return;
      }

      if (tool.renderPane) {
        const shouldFocus = options?.focus ?? true;
        setOpenToolIds((prev) => {
          if (prev.includes(toolId)) {
            return prev;
          }
          return [...prev, toolId];
        });
        if (shouldFocus) {
          setFocusedPaneId(toolId);
        }
      }

      if (tool.activate) {
        tool.activate({
          commands,
          openUploadDialog: () => setIsUploadDialogOpen(true),
        });
      }
    },
    [commands, toolById],
  );

  const handleCloseTool = useCallback(
    (toolId: string) => {
      setOpenToolIds((prev) => {
        if (!prev.includes(toolId)) {
          return prev;
        }
        const next = prev.filter((id) => id !== toolId);
        if (focusedPaneId === toolId) {
          const fallbackId = next[0] ?? fileTabs[0]?.id ?? EMPTY_PANE_ID;
          setFocusedPaneId(fallbackId);
        }
        return next;
      });
    },
    [fileTabs, focusedPaneId],
  );

  const handleCloseFileTab = useCallback(
    (tabId: string) => {
      setFileTabs((prev) => {
        const index = prev.findIndex((tab) => tab.id === tabId);
        if (index === -1) {
          return prev;
        }
        const nextTabs = [...prev.slice(0, index), ...prev.slice(index + 1)];
        if (focusedPaneId === tabId) {
          const fallbackTab = nextTabs[index] ?? nextTabs[index - 1];
          const fallbackTabId =
            fallbackTab?.id ?? openToolIds[0] ?? EMPTY_PANE_ID;
          setFocusedPaneId(fallbackTabId);
        }
        return nextTabs;
      });
    },
    [focusedPaneId, openToolIds],
  );

  const handleCloseHistoryTab = useCallback(
    (tabId: string) => {
      setFileTabs((prev) => {
        const index = prev.findIndex((tab) => tab.id === tabId);
        if (index === -1) {
          return prev;
        }
        const nextTabs = [...prev.slice(0, index), ...prev.slice(index + 1)];
        if (focusedPaneId === tabId) {
          const fallbackTab = nextTabs[index] ?? nextTabs[index - 1];
          const fallbackTabId =
            fallbackTab?.id ?? openToolIds[0] ?? EMPTY_PANE_ID;
          setFocusedPaneId(fallbackTabId);
        }
        return nextTabs;
      });
    },
    [focusedPaneId, openToolIds],
  );

  const files = useMemo(
    () => openableFilePaths(treeEntries ?? [], showHiddenFiles),
    [treeEntries, showHiddenFiles],
  );

  const commandGroups = useMemo<CommandMenuGroup[]>(() => {
    const toolItem = (tool: ProjectTool) => {
      const Icon = tool.icon;
      return {
        id: tool.id,
        label: tool.commandLabel,
        description: tool.description,
        icon: <Icon className="h-4 w-4" />,
        keywords: tool.keywords,
        run: () => handleOpenTool(tool.id),
      };
    };

    const fileItems = files.map((filename) => ({
      id: `file:${filename}`,
      label: filename,
      icon: <ProjectFileIcon filename={filename} className="h-4 w-4" />,
      keywords: [filename],
      run: () => handleOpenFile(filename),
    }));

    // A tool with a pane is somewhere you go; one that only activates (new
    // document, new folder, upload) is something you do. So the group falls
    // out of the tool, and a new one lands in the right half by itself.
    const actionItems = [
      ...[...toolById.values()]
        .filter((tool) => !tool.renderPane)
        .map(toolItem),
      ...(!task
        ? [
            {
              id: "save-checkpoint",
              label: "Save a checkpoint",
              description: "Save the current state of the project",
              icon: <MapPinPlus className="h-4 w-4" />,
              keywords: ["checkpoint", "save", "snapshot", "restore point"],
              // Show the tab too: the new checkpoint appears in the rail, and
              // landing there is how a member sees that it worked.
              run: () => {
                showPanelTab("checkpoints");
                setIsCheckpointDialogOpen(true);
              },
            },
          ]
        : []),
      ...(!task
        ? [
            {
              id: "rename-project",
              label: "Rename project",
              icon: <PencilIcon className="h-4 w-4" />,
              run: () => {
                setRenameDraft(project.name);
                setIsRenameDialogOpen(true);
              },
            },
            ...(canManage
              ? [
                  {
                    id: "delete-project",
                    label: "Delete project",
                    icon: <Trash2 className="h-4 w-4" />,
                    run: () => setIsDeleteDialogOpen(true),
                  },
                ]
              : []),
          ]
        : []),
    ];

    const toolItems = [...toolById.values()]
      .filter((tool) => tool.renderPane)
      .map(toolItem);

    const settingsItems = [
      {
        id: "toggle-vim-mode",
        label: vimMode ? "Disable Vim Mode" : "Enable Vim Mode",
        icon: <KeyboardIcon className="h-4 w-4" />,
        keywords: ["vim", "editor", "keybindings", "mode"],
        run: () => setVimMode(!vimMode),
      },
    ];

    return [
      { id: "actions", label: "Actions", items: actionItems },
      { id: "tools", label: "Tools", items: toolItems },
      { id: "files", label: "Files", items: fileItems },
      { id: "settings", label: "Settings", items: settingsItems },
    ].filter((group) => group.items.length > 0);
  }, [
    files,
    canManage,
    handleOpenFile,
    handleOpenTool,
    project.name,
    showPanelTab,
    vimMode,
    setVimMode,
    toolById,
    task,
  ]);

  useProjectShellCommands(commandGroups);

  const filesPanelDefaultSize = 20;
  const filesPanelSize = filesPanelManualSize ?? filesPanelDefaultSize;
  const builderPanelDefaultSize = 26;
  const builderPanelSize = builderPanelManualSize ?? builderPanelDefaultSize;
  const mainPanelDefaultSize =
    100 - filesPanelSize - (isBuilderPaneOpen ? builderPanelSize : 0);

  const openFileNames = useMemo(
    () => [
      ...new Set(
        fileTabs
          .filter((tab) => tab.kind !== "history")
          .map((tab) => tab.filename),
      ),
    ],
    [fileTabs],
  );

  const activeFile = useMemo(() => {
    if (focusedPaneId.startsWith(FILE_TAB_PREFIX)) {
      return focusedPaneId.slice(FILE_TAB_PREFIX.length);
    }
    return null;
  }, [focusedPaneId]);

  useEffect(() => {
    if (hasOpenPanes) {
      if (!allPaneIds.includes(focusedPaneId)) {
        const fallbackId = allPaneIds[0] ?? EMPTY_PANE_ID;
        setFocusedPaneId(fallbackId);
      }
    } else if (focusedPaneId !== EMPTY_PANE_ID) {
      setFocusedPaneId(EMPTY_PANE_ID);
    }
  }, [allPaneIds, focusedPaneId, hasOpenPanes]);

  // Inside a stack the tab is the pane's chrome, so it reads like the window
  // toolbar it stands in for: file icon, name, unsaved-changes dot.
  const renderTabTitle = useCallback(
    ({ tabKey }: { tabKey: PaneId }) => {
      const tool = toolById.get(tabKey);
      const fileTab = fileTabs.find((tab) => tab.id === tabKey);
      if (tool || !fileTab) {
        return <span className="truncate">{tool?.title ?? tabKey}</span>;
      }
      return (
        <span
          className="flex min-w-0 items-center gap-1.5"
          title={fileTab.filename}
        >
          <ProjectFileIcon
            filename={fileTab.filename}
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          />
          <span className="truncate">{basename(fileTab.title)}</span>
          {dirtyFiles.has(fileTab.filename) ? (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
          ) : null}
        </span>
      );
    },
    [fileTabs, dirtyFiles, toolById],
  );

  // A pane's toolbar controls, which follow it into a stack: the tab bar
  // renders the showing tab's, so a tabbed file keeps its history button and a
  // tabbed tool keeps its own.
  const paneActions = useCallback(
    (paneId: PaneId): ReactNode => {
      const tool = toolById.get(paneId);
      if (tool) {
        return null;
      }

      const fileTab = fileTabs.find((tab) => tab.id === paneId);
      if (!fileTab) {
        return null;
      }
      const docUri = inodeAt.get(fileTab.filename)?.uri;
      const hasDoc = isAutomergeUrl(docUri);
      return (
        <>
          <PaneActions paneId={fileTab.id} />
          {isAutomergeUrl(docUri) && EDITOR_KINDS.has(fileTab.kind) ? (
            <CommentsButton
              url={docUri}
              open={commentsFor === fileTab.filename}
              onToggle={() =>
                setCommentsFor((open) =>
                  open === fileTab.filename ? null : fileTab.filename,
                )
              }
            />
          ) : null}
          {hasDoc && fileTab.kind !== "history" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              title="View history"
              onClick={() => handleOpenHistory(fileTab.filename)}
            >
              <History className="h-4 w-4" />
            </Button>
          ) : null}
          {fileTab.kind === "code" ? (
            <TooltipProvider delayDuration={300}>
              <Tabs
                value={
                  initialFile === fileTab.filename && fileView === "model"
                    ? "model"
                    : "source"
                }
                onValueChange={(value) => {
                  void router.navigate({
                    to: "/p/$projectId/$branch/explorer/files/$",
                    params: { projectId, branch, _splat: fileTab.filename },
                    search: {
                      view: value === "model" ? "model" : undefined,
                    },
                  });
                }}
              >
                <TabsList className="h-8 bg-transparent p-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <TabsTrigger
                        value="model"
                        aria-label="Model view"
                        className="size-8 p-0 data-[state=active]:bg-muted"
                      >
                        <Box className="size-4" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Model</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <TabsTrigger
                        value="source"
                        aria-label="Source view"
                        className="size-8 p-0 data-[state=active]:bg-muted"
                      >
                        <Code2 className="size-4" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Source</TooltipContent>
                  </Tooltip>
                </TabsList>
              </Tabs>
            </TooltipProvider>
          ) : null}
        </>
      );
    },
    [
      commentsFor,
      fileTabs,
      fileView,
      handleOpenHistory,
      initialFile,
      inodeAt,
      projectId,
      router,
      task,
      toolById,
    ],
  );

  const renderTabToolbarControls = useCallback(
    ({ tabs, activeTabIndex }: TabGroupContext<PaneId>) =>
      paneActions(tabs[activeTabIndex] ?? ""),
    [paneActions],
  );

  // Clicking a tab is a click into that pane: focus lands there even when it
  // was on another split entirely.
  const focusTab = useCallback(
    (paneId: string) => {
      setFocusedPaneId(paneId);
      const filename = paneId.startsWith(FILE_TAB_PREFIX)
        ? paneId.slice(FILE_TAB_PREFIX.length)
        : undefined;
      if (filename && filename !== initialFile) {
        void router.navigate({
          to: "/p/$projectId/$branch/explorer/files/$",
          params: { projectId, branch, _splat: filename },
          search: { view: undefined },
        });
      }
    },
    [initialFile, projectId, router, task],
  );
  const renderTabButton = useCallback<TabButtonRenderer<PaneId>>(
    (props) => <PaneTab {...props} onFocus={focusTab} />,
    [focusTab],
  );

  const renderPane = useCallback(
    (paneId: PaneId, path: MosaicPath) => {
      const tabbed = isTabbedTile(mosaicLayout, path);
      const tool = toolById.get(paneId);
      if (tool && tool.renderPane) {
        const IconComponent = tool.icon;
        return (
          <PaneFrame
            path={path}
            tabbed={tabbed}
            title={tool.title}
            icon={<IconComponent className="h-4 w-4 text-muted-foreground" />}
            actions={paneActions(tool.id)}
            onClose={() => handleCloseTool(tool.id)}
            onFocus={() => focusTab(tool.id)}
          >
            {tool.renderPane({
              project,
              changeProject,
              indexUrl,
              onOpenPath: handleOpenPath,
            })}
          </PaneFrame>
        );
      }

      const fileTab = fileTabs.find((tab) => tab.id === paneId);
      if (fileTab) {
        const inode = inodeAt.get(fileTab.filename);
        const uri = inode?.uri;
        const automergeUrl = isAutomergeUrl(uri)
          ? (uri as AutomergeUrl)
          : undefined;
        const externalHref = resolveProjectFileHref(uri);
        const typeLabel =
          fileTab.kind === "doc"
            ? "Document"
            : fileTab.kind === "code" || fileTab.kind === "plaintext"
              ? "Source"
              : fileTab.kind === "sketch"
                ? "Sketch"
                : fileTab.kind === "pdf"
                  ? "PDF"
                  : fileTab.kind === "records"
                    ? "Table"
                    : "History";
        const fallbackContent = (
          <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
            {externalHref ? (
              <a
                href={externalHref}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Open {fileTab.filename}
              </a>
            ) : (
              `${typeLabel} file "${fileTab.filename}" not found.`
            )}
          </div>
        );
        let content: ReactNode;
        if (fileTab.kind === "history") {
          content = automergeUrl ? (
            <HistoryPane docName={fileTab.filename} docUrl={automergeUrl} />
          ) : (
            <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
              History is only available for Automerge documents.
            </div>
          );
        } else if (EDITOR_KINDS.has(fileTab.kind)) {
          // The editor opens with or without a doc behind it: a path with no
          // inode in the project doc is usually a mounted library file, which
          // it reads through the filesystem and shows read-only. It falls back
          // to "not found" itself when there is genuinely nothing there.
          content = (
            <EditorPaneWithDirty
              paneId={fileTab.id}
              filename={fileTab.filename}
              url={automergeUrl}
              plainText={fileTab.kind === "plaintext"}
              setDirtyFiles={setDirtyFiles}
              onOpenPath={handleOpenPath}
              revealSelection={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.selection
                  : undefined
              }
              revealHeading={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.heading
                  : undefined
              }
              revealThread={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.thread
                  : undefined
              }
            />
          );
        } else if (fileTab.kind === "records") {
          // The pane opens the file itself: its MIME, not this extension,
          // decides whether it is a table.
          content = <RecordsPane filename={fileTab.filename} />;
        } else if (fileTab.kind === "pdf") {
          content = (
            <ExplorerPdfPane
              paneId={fileTab.id}
              filename={fileTab.filename}
              inode={inode}
            />
          );
        } else {
          content = automergeUrl ? (
            <SketchPane
              paneId={fileTab.id}
              filename={fileTab.filename}
              url={automergeUrl}
              reveal={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.shapes
                  : undefined
              }
            />
          ) : (
            fallbackContent
          );
        }
        if (fileTab.kind === "code") {
          if (initialFile === fileTab.filename && fileView === "model") {
            content = (
              <BearModelPane
                key={`${indexUrl}:${fileTab.filename}`}
                entryPoint={fileTab.filename}
                autoRun
                onOpenPath={handleOpenPath}
                markup={markupHost}
              />
            );
          }
        }
        return (
          <PaneFrame
            path={path}
            tabbed={tabbed}
            title={fileTab.title}
            dirty={dirtyFiles.has(fileTab.filename)}
            icon={
              fileTab.kind === "history" ? (
                <History className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ProjectFileIcon
                  filename={fileTab.filename}
                  className="h-4 w-4 text-muted-foreground"
                />
              )
            }
            actions={paneActions(fileTab.id)}
            onClose={() =>
              fileTab.kind === "history"
                ? handleCloseHistoryTab(fileTab.id)
                : handleCloseFileTab(fileTab.id)
            }
            onFocus={() => focusTab(fileTab.id)}
          >
            {automergeUrl && commentsFor === fileTab.filename ? (
              <div className="relative h-full">
                {content}
                <FileComments
                  url={automergeUrl}
                  path={fileTab.filename}
                  onClose={() => setCommentsFor(null)}
                  onReveal={(threadId) => {
                    // Text threads are revealed in the text, not the model.
                    if (fileView === "model") {
                      void router.navigate({
                        to: "/p/$projectId/$branch/explorer/files/$",
                        params: { projectId, branch, _splat: fileTab.filename },
                        search: { view: undefined },
                      });
                    }
                    setJumpTarget({
                      path: fileTab.filename,
                      thread: { threadId },
                    });
                  }}
                />
              </div>
            ) : (
              content
            )}
          </PaneFrame>
        );
      }

      return (
        <MosaicWindow
          path={path}
          title="Pane unavailable"
          draggable={false}
          className="h-full"
          renderToolbar={() => (
            <PaneToolbar
              title="Pane unavailable"
              onClose={() => {
                setMosaicLayout((prev) =>
                  adjustLayoutForActivePanes(
                    prev,
                    allPaneIds.filter((id) => id !== paneId),
                  ),
                );
              }}
              onFocus={() => {
                /* noop */
              }}
            />
          )}
        >
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Unable to load pane {paneId}
          </div>
        </MosaicWindow>
      );
    },
    [
      allPaneIds,
      fileTabs,
      dirtyFiles,
      mosaicLayout,
      handleCloseHistoryTab,
      handleCloseFileTab,
      handleCloseTool,
      handleOpenHistory,
      handleOpenPath,
      inodeAt,
      fileView,
      initialFile,
      markupHost,
      paneActions,
      jumpTarget,
      commentsFor,
      router,
      projectId,
      branch,
      project,
      changeProject,
      indexUrl,
      focusTab,
      toolById,
    ],
  );

  return (
    <AskBearProvider value={askBear}>
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &ldquo;{project.name}&rdquo; and all
              of its files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleteProjectMutation.isPending}
              onClick={() => deleteProjectMutation.mutate()}
            >
              {deleteProjectMutation.isPending
                ? "Deleting..."
                : "Delete project"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              handleRename(renameDraft.trim() || "Untitled Project");
              setIsRenameDialogOpen(false);
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename project</DialogTitle>
            </DialogHeader>
            <Input
              aria-label="Project name"
              value={renameDraft}
              onChange={(event) => setRenameDraft(event.target.value)}
              autoFocus
            />
            <DialogFooter>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-hidden">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel
              id="files"
              defaultSize={filesPanelSize}
              minSize={10}
              onResize={(panelSize) => {
                setFilesPanelManualSize(panelSize.asPercentage);
              }}
            >
              <ProjectFileList
                files={treeFiles}
                commands={commands}
                expansion={expansion}
                tab={task ? "files" : filesPanelTab}
                onSelectTab={task ? undefined : showPanelTab}
                checkpoints={checkpoints}
                onOpenFile={handleOpenFile}
                onOpenAsPlainText={handleOpenAsPlainText}
                onOpenHistory={handleOpenHistory}
                openFiles={openFileNames}
                activeFile={activeFile ?? undefined}
                showHidden={showHiddenFiles}
                checkpointDialogOpen={isCheckpointDialogOpen}
                onCheckpointDialogOpenChange={setIsCheckpointDialogOpen}
                uploadDialogOpen={isUploadDialogOpen}
                onUploadDialogOpenChange={setIsUploadDialogOpen}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel
              id="main"
              defaultSize={mainPanelDefaultSize}
              minSize={30}
            >
              <div className="flex h-full flex-col">
                <Mosaic
                  className="mosaic mosaic-shadcn-theme h-full"
                  renderTile={renderPane}
                  renderTabTitle={renderTabTitle}
                  renderTabButton={renderTabButton}
                  // Replaces the library's add/split/remove cluster with the
                  // showing pane's own controls; the handle that drags the
                  // whole stack stays.
                  renderTabToolbarControls={renderTabToolbarControls}
                  canClose={() => "canClose"}
                  value={hasOpenPanes ? mosaicLayout : null}
                  onChange={handleLayoutChange}
                  zeroStateView={
                    <Empty className="h-full">
                      <EmptyDescription>
                        Select a file from the sidebar
                      </EmptyDescription>
                    </Empty>
                  }
                />
              </div>
            </ResizablePanel>
            {isBuilderPaneOpen ? <ResizableHandle withHandle /> : null}
            {isBuilderPaneOpen ? (
              <ResizablePanel
                id="builder"
                defaultSize={builderPanelSize}
                minSize={18}
                onResize={(panelSize) => {
                  setBuilderPanelManualSize(panelSize.asPercentage);
                }}
              >
                <div className="flex h-full flex-col">
                  <PaneSuspense>
                    <BuilderPane
                      projectId={projectId}
                      taskId={task?.id}
                      project={project}
                      indexUrl={indexUrl}
                      chatId={
                        chats.open?.kind === "thread" ? chats.open.id : null
                      }
                      chats={chats}
                      checkpoints={checkpoints}
                      activeFile={activeFile}
                      onShow={handleShow}
                      onScopeMove={followConversation}
                    />
                  </PaneSuspense>
                </div>
              </ResizablePanel>
            ) : null}
          </ResizablePanelGroup>
        </main>

        {/*<TanStackRouterDevtools /> */}
      </div>
    </AskBearProvider>
  );
}

/**
 * A pane's frame. A window carries its own toolbar; a tab in a stack is
 * already titled, closed, and dragged by the tab bar above it, so it gets the
 * body alone.
 */
function PaneFrame({
  path,
  tabbed,
  title,
  icon,
  dirty,
  actions,
  onClose,
  onFocus,
  children,
}: {
  path: MosaicPath;
  tabbed: boolean;
  title: string;
  icon?: ReactNode;
  dirty?: boolean;
  actions?: ReactNode;
  onClose: () => void;
  onFocus: () => void;
  children: ReactNode;
}) {
  const body = (
    <div className="flex h-full flex-col" onPointerDownCapture={onFocus}>
      <PaneSuspense>{children}</PaneSuspense>
    </div>
  );

  if (tabbed) {
    return body;
  }

  return (
    <MosaicWindow
      path={path}
      title={title}
      draggable={false}
      className="h-full"
      renderToolbar={() => (
        <PaneToolbar
          title={title}
          icon={icon}
          dirty={dirty}
          actions={actions}
          onClose={onClose}
          onFocus={onFocus}
        />
      )}
    >
      {body}
    </MosaicWindow>
  );
}

/** The PDF's bytes are behind its blob doc once it has been annotated. */
function ExplorerPdfPane({
  paneId,
  filename,
  inode,
}: {
  paneId: string;
  filename: string;
  inode: PlayFileInode | undefined;
}) {
  const resolved = useBlobSrc(inode);
  return resolved ? (
    <PdfPane paneId={paneId} filename={filename} src={resolved.src} />
  ) : null;
}

function EditorPaneWithDirty({
  paneId,
  filename,
  url,
  plainText,
  setDirtyFiles,
  onOpenPath,
  revealSelection,
  revealHeading,
  revealThread,
}: {
  paneId: string;
  filename: string;
  /** Absent for a file with no Automerge doc, e.g. mounted library content. */
  url?: AutomergeUrl;
  /** Open prose in Monaco anyway, for "open as plain text". */
  plainText: boolean;
  setDirtyFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Open another workspace file, e.g. a "go to definition" target. */
  onOpenPath: (path: string, selection?: EditorSelection) => void;
  /** Where to put the cursor once this file loads (a jump landed here). */
  revealSelection?: EditorSelection;
  /** A heading to scroll to, for prose. */
  revealHeading?: { text: string };
  /** A comment thread to open. */
  revealThread?: { threadId: string };
}) {
  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setDirtyFiles((prev) => {
        const has = prev.has(filename);
        if (dirty === has) return prev;
        const next = new Set(prev);
        if (dirty) {
          next.add(filename);
        } else {
          next.delete(filename);
        }
        return next;
      });
    },
    [filename, setDirtyFiles],
  );

  return (
    <EditorPane
      paneId={paneId}
      filename={filename}
      url={url}
      plainText={plainText}
      onDirtyChange={handleDirtyChange}
      onOpenPath={onOpenPath}
      revealSelection={revealSelection}
      revealHeading={revealHeading}
      revealThread={revealThread}
    />
  );
}

function PaneToolbar({
  title,
  icon,
  dirty,
  actions,
  onClose,
  onFocus,
}: {
  title: string;
  icon?: ReactNode;
  dirty?: boolean;
  actions?: ReactNode;
  onClose: () => void;
  onFocus: () => void;
}) {
  const windowContext = useContext(MosaicWindowContext);

  const connectDragSource =
    windowContext?.mosaicWindowActions.connectDragSource;

  const dragRegion = (
    <div className="flex h-full min-w-0 flex-1 cursor-grab select-none items-center gap-2 pr-2 text-sm font-medium text-foreground">
      <span className="relative shrink-0">
        {icon}
        {dirty ? (
          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-foreground" />
        ) : null}
      </span>
      <span className="truncate">{title}</span>
    </div>
  );

  const dragSource = connectDragSource
    ? connectDragSource(dragRegion)
    : dragRegion;

  return (
    <div
      className="flex h-10 w-full items-center border-b border-border bg-card px-2"
      onPointerDownCapture={onFocus}
    >
      {dragSource}
      {actions ? (
        <div className="flex items-center gap-1 pr-1">{actions}</div>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        title="Close pane"
        onClick={onClose}
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}
